/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode, useCallback, forwardRef, useState, useEffect } from 'react'
import {
  SnackbarKey,
  SnackbarProvider as NotistackProvider,
  useSnackbar as useNotistack
} from 'notistack'
import { Alert, AlertColor } from '@mui/material'
import {
  calculateGridMargin,
  calculateMaxWidth
} from '@/shared/utils/gridCalculations'
import IconButton from '@/shared/components/IconButton/IconButton'
import { getIconBySeverity } from '@/shared/utils/getIconBySeverity'
import { snackbarStyles } from './Snackbar.styles'
import styles from './Snackbar.module.scss'

interface SnackbarOptions {
  /** Stays up until closed, for something still under way, such as a login. */
  persist?: boolean
}

interface SnackbarContextType {
  showSnackbar: (
    title: string,
    message: string,
    severity?: AlertColor,
    options?: SnackbarOptions
  ) => SnackbarKey
  closeSnackbar: (key: SnackbarKey) => void
}

interface CustomSnackbarProps {
  id: number | string
  title: string
  message: string
  severity: AlertColor
}

const CustomSnackbar = forwardRef<HTMLDivElement, CustomSnackbarProps>(
  ({ id, title, message, severity }, ref) => {
    const [maxWidth, setMaxWidth] = useState<string>('')
    const [gridMargin, setGridMargin] = useState<string>('0px')
    const { closeSnackbar } = useNotistack()

    useEffect(() => {
      const updateMaxWidth = () => setMaxWidth(calculateMaxWidth())
      updateMaxWidth()
      window.addEventListener('resize', updateMaxWidth)
      return () => window.removeEventListener('resize', updateMaxWidth)
    }, [])

    useEffect(() => {
      const updateMargin = () => setGridMargin(calculateGridMargin())
      updateMargin()
      window.addEventListener('resize', updateMargin)
      return () => window.removeEventListener('resize', updateMargin)
    }, [])

    return (
      <div ref={ref}>
        <Alert
          severity={severity}
          icon={getIconBySeverity(severity, 'icon-big')}
          variant="filled"
          sx={{
            ...snackbarStyles,
            maxWidth: `${maxWidth} !important`,
            minWidth: `${maxWidth} !important`,
            marginRight: `${gridMargin} !important`
          }}
          action={
            <IconButton
              color="primary"
              icon="las la-times icon-big"
              onClick={() => closeSnackbar(id)}
            />
          }
        >
          <div className={styles.snackbarTextContainer}>
            <p className={`${styles.snackbarTitle} body-bold-medium`}>
              {title}
            </p>
            <p className={`${styles.snackbarMessage} body-small`}>{message}</p>
          </div>
        </Alert>
      </div>
    )
  }
)

CustomSnackbar.displayName = 'CustomSnackbar'

export function SnackbarProvider({
  children
}: {
  children: ReactNode
}): JSX.Element {
  return (
    <NotistackProvider
      maxSnack={3}
      autoHideDuration={5000}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      classes={{
        containerRoot: styles.customSnackbarContainer
      }}
    >
      {children}
    </NotistackProvider>
  )
}

export const useSnackbar = (): SnackbarContextType => {
  const { enqueueSnackbar, closeSnackbar } = useNotistack()

  const showSnackbar = useCallback(
    (
      title: string,
      message: string,
      severity: AlertColor = 'info',
      options: SnackbarOptions = {}
    ) =>
      enqueueSnackbar(message, {
        variant: severity,
        persist: options.persist,
        content: key => (
          <CustomSnackbar
            id={key}
            key={key}
            title={title}
            message={message}
            severity={severity}
          />
        ),
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      }),
    [enqueueSnackbar]
  )

  return { showSnackbar, closeSnackbar }
}
